'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PDFDocument, degrees } from 'pdf-lib';
import Icon from '@/components/ui/AppIcon';
import { TOOL_COLORS } from '@/constants/toolColors';
import Script from 'next/script';
import UploadZone from '@/components/pdf/UploadZone';
import { useToast } from '@/components/ui/Toast';
import { isEncryptedPDF } from '@/utils/pdf';
import PDFViewerModal from '@/components/pdf/PDFViewerModal';
import { ensurePdfWorker, getPdfjsLib, renderPageThumbnail } from '@/utils/pdfThumbnailUtils';
import { FileCard } from '@/components/pdf/FileCard';
import { getPdfMeta, useEncryptedPdfToast } from '@/constants/pdfConfig';

const colors = TOOL_COLORS.rotate;

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type?: string;
  file: File;
  pageCount: number;
}

interface PageThumbnail {
  id: string;
  originalIndex: number;
  url: string;
  rotation: number; // user-applied rotation: 0 | 90 | 180 | 270
  intrinsicRotation: number; // PDF-embedded /Rotate value
  width: number;
  height: number;
}

const MAX_SIZE = 100 * 1024 * 1024; // 100MB

const ROTATE_THEME = {
  primary: '#F59E0B',
  primaryLight: '#FFFBEB',
  primaryBorder: '#FDE68A',
  primaryShadow: 'rgba(245, 158, 11, 0.28)',
};

export default function RotatePDFUploader() {
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [pages, setPages] = useState<PageThumbnail[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [processedPdfBlob, setProcessedPdfBlob] = useState<Blob | null>(null);
  // Cached pdf.js document — avoids reloading the file on every thumbnail re-render
  const pdfDocRef = useRef<any>(null);

  const { showToast } = useToast();
  const { showEncryptedPdfToast } = useEncryptedPdfToast();

  /** Handle File Upload */
  const handleFiles = useCallback(
    async (newFiles: FileList | null) => {
      if (!newFiles || newFiles.length === 0) return;

      const f = newFiles[0];

      if (
        !(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) ||
        f.size > MAX_SIZE
      ) {
        showToast('Invalid file type or file too large (max 100MB).', 'error');
        return;
      }

      try {
        // Pre-flight: check for encryption
        const alreadyEncrypted = await isEncryptedPDF(f);
        if (alreadyEncrypted) {
          showEncryptedPdfToast('rotating');
          return;
        }

        // Cleanup old thumbnails
        pages.forEach((p) => URL.revokeObjectURL(p.url));
        setPages([]);

        const meta = await getPdfMeta(f);
        setFile(meta);
        setIsDone(false);
      } catch (err) {
        console.error('Error reading PDF:', err);
        showToast('Failed to read PDF file. It might be password protected.', 'error');
      }
    },
    [pages, showToast]
  );

  /** Lazy Thumbnail Generation */
  useEffect(() => {
    if (!file) return;

    let cancelled = false;
    // Track URLs we create during generation so we can revoke them on cleanup.
    // Using a separate array (not loadedPages) avoids replacing the full React
    // state on every page, which would overwrite mid-load user rotations.
    const generatedUrls: string[] = [];

    const generateThumbnails = async () => {
      try {
        const bytes = await file.file.arrayBuffer();
        const pdfjsLib = getPdfjsLib();
        if (!pdfjsLib) {
          console.error('pdfjsLib not found');
          return;
        }

        ensurePdfWorker(pdfjsLib);
        const loadingTask = pdfjsLib.getDocument({ data: bytes });
        const pdf = await loadingTask.promise;
        pdfDocRef.current = pdf; // cache for thumbnail re-renders

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;

          // No rotation param → pdf.js uses page's intrinsic /Rotate automatically
          const result = await renderPageThumbnail(pdf, i - 1);
          if (!result) continue;

          generatedUrls.push(result.url);

          const p: PageThumbnail = {
            id: crypto.randomUUID(),
            originalIndex: i - 1,
            url: result.url,
            rotation: 0,
            intrinsicRotation: result.intrinsicRotation,
            width: result.width,
            height: result.height,
          };

          // IMPORTANT: append to existing state, do NOT replace it.
          // Replacing (setPages([...loadedPages])) would overwrite any
          // rotation changes the user applied to earlier pages mid-load.
          setPages((prev) => (cancelled ? prev : [...prev, p]));
          await new Promise((r) => setTimeout(r, 10));
        }
      } catch (err) {
        console.error('Thumbnail generation error:', err);
      }
    };

    generateThumbnails();

    return () => {
      cancelled = true;
      // Revoke any URLs we generated. If reRenderSingleThumbnail already revoked
      // one, the browser silently ignores the duplicate call — no harm done.
      generatedUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [file]);

  // ── Thumbnail re-render helper ──────────────────────────────────────────────

  /**
   * Re-renders a single page's thumbnail with the given total rotation baked in.
   * totalRotation = (intrinsicRotation + userRotation) % 360.
   * This replaces the CSS-transform approach so thumbnails exactly match the viewer.
   */
  const reRenderSingleThumbnail = useCallback(async (pageIndex: number, totalRotation: number) => {
    const pdf = pdfDocRef.current;
    if (!pdf) return;
    const result = await renderPageThumbnail(pdf, pageIndex, { rotation: totalRotation });
    if (!result) return;
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIndex) return p;
        URL.revokeObjectURL(p.url);
        return { ...p, url: result.url, width: result.width, height: result.height };
      })
    );
  }, []);

  /** Page Actions */
  const rotatePage = useCallback(
    (index: number) => {
      setPages((prev) => {
        const page = prev[index];
        if (!page) return prev;
        const newUserRotation = (page.rotation + 90) % 360;
        const totalRotation = (page.intrinsicRotation + newUserRotation) % 360;
        // Re-render thumbnail asynchronously so the image matches the viewer
        setTimeout(() => reRenderSingleThumbnail(index, totalRotation), 0);
        return prev.map((p, i) => (i === index ? { ...p, rotation: newUserRotation } : p));
      });
      if (typeof window !== 'undefined' && (window as any).clarity) {
        (window as any).clarity('set', 'rotate_individual', 'true');
      }
    },
    [reRenderSingleThumbnail]
  );

  const rotateAll = useCallback(() => {
    setPages((prev) => {
      const updated = prev.map((p) => ({ ...p, rotation: (p.rotation + 90) % 360 }));
      // Schedule sequential re-renders to avoid flooding the renderer
      updated.forEach((p, i) => {
        const totalRotation = (p.intrinsicRotation + p.rotation) % 360;
        setTimeout(() => reRenderSingleThumbnail(i, totalRotation), i * 40);
      });
      return updated;
    });
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity('set', 'rotate_all', 'true');
    }
  }, [reRenderSingleThumbnail]);

  const resetAll = useCallback(() => {
    setPages((prev) => {
      const updated = prev.map((p) => ({ ...p, rotation: 0 }));
      // Re-render each page back to intrinsic-only rotation
      updated.forEach((p, i) => {
        setTimeout(() => reRenderSingleThumbnail(i, p.intrinsicRotation), i * 40);
      });
      return updated;
    });
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity('set', 'rotate_reset', 'true');
    }
  }, [reRenderSingleThumbnail]);

  /** Final PDF Processing */
  const handleRotate = useCallback(async () => {
    if (!file || pages.length === 0) return;

    try {
      setIsProcessing(true);
      setProgress(0);

      const arrayBuffer = await file.file.arrayBuffer();
      const sourcePdf = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const targetPdf = await PDFDocument.create();

      const pageCount = sourcePdf.getPageCount();
      for (let i = 0; i < pageCount; i++) {
        const [copiedPage] = await targetPdf.copyPages(sourcePdf, [i]);
        const p = pages[i];

        // Apply rotation if needed
        if (p && p.rotation !== 0) {
          const currentRotation = copiedPage.getRotation().angle;
          copiedPage.setRotation(degrees(currentRotation + p.rotation));
        }

        targetPdf.addPage(copiedPage);
        setProgress(Math.round(((i + 1) / pageCount) * 100));
        await new Promise((resolve) => setTimeout(resolve, 5));
      }

      const pdfBytes = await targetPdf.save();
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });

      // Save blob for download button
      setProcessedPdfBlob(blob);

      setIsDone(true);
    } catch (err) {
      console.error('PDF processing error:', err);
      showToast('Failed to process PDF. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [file, pages, showToast]);

  const handleReset = useCallback(() => {
    // Clean up thumbnails
    pages.forEach((p) => URL.revokeObjectURL(p.url));
    setFile(null);
    setPages([]);
    setIsDone(false);
    setProgress(0);
    setProcessedPdfBlob(null);
  }, [pages]);

  const handleDownload = useCallback(() => {
    if (!processedPdfBlob) return;

    const url = URL.createObjectURL(processedPdfBlob);

    const a = document.createElement('a');

    a.href = url;
    a.download = `rotated_${file?.name}`;

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [processedPdfBlob, file]);

  return (
    <div className="w-full max-w-6xl mx-auto px-4">
      <Script
        src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.min.mjs"
        type="module"
        strategy="afterInteractive"
      />

      {file && (
        <PDFViewerModal
          file={file}
          isOpen={viewerOpen}
          currentPage={currentPage}
          onClose={() => setViewerOpen(false)}
          onPageChange={setCurrentPage}
          pageRotation={pages[currentPage]?.rotation ?? 0}
          onRotate={(i) => rotatePage(i)}
          isPageRotated={(i) => (pages[i]?.rotation ?? 0) !== 0}
        />
      )}

      {/* Upload */}
      {!file && (
        <UploadZone
          onFilesSelected={handleFiles}
          multiple={false}
          accentColor={colors.primary}
          iconName="ArrowPathIcon"
          title="Drop PDF file here"
          subtitle="or click to browse — single file only"
          buttonText="Select PDF File"
          dragTitle="Drop your PDF here!"
        />
      )}

      {/* ================= WORK AREA ================= */}
      {file && !isDone && (
        <div className="flex flex-col lg:flex-row gap-6 mt-4">
          {/* ================= LEFT ================= */}
          <div className="flex-1 min-w-0">
            {/* Header (same pattern as RemovePagePDF) */}
            <FileCard file={file} onRemove={handleReset} theme={ROTATE_THEME} />

            {/* Thumbnails (same grid feel as RemovePagePDF) */}
            <div className="max-h-[70vh] overflow-y-auto pr-2 pb-20 lg:pb-0">
              <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {pages.length === 0
                  ? Array.from({ length: file.pageCount }).map((_, i) => (
                      <div
                        key={i}
                        className="relative rounded-xl animate-pulse aspect-[100/140] bg-brand-surface"
                      />
                    ))
                  : pages.map((page, i) => {
                      const isRotated = page.rotation !== 0;

                      return (
                        <div
                          key={page.id}
                          className="relative border rounded-xl overflow-hidden aspect-[100/140] group cursor-pointer transition"
                          style={{
                            borderColor: isRotated ? colors.primary : '#EEEEF5',
                            borderWidth: isRotated ? '2px' : '1px',
                          }}
                          onClick={() => {
                            setCurrentPage(i);
                            setViewerOpen(true);
                          }}
                        >
                          <img
                            src={page.url}
                            alt={`Page ${i + 1}`}
                            className="w-full h-full object-contain bg-slate-50"
                          />

                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                            <Icon name="EyeIcon" size={20} className="text-white" />
                          </div>

                          {/* Page Number */}
                          <span className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-brand-muted">
                            {i + 1}
                          </span>

                          {/* Rotate Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              rotatePage(i);
                            }}
                            className="absolute top-2 right-2 z-30 w-6 h-6 rounded-full flex items-center justify-center text-white text-[12px]"
                            style={{ background: colors.primary }}
                          >
                            <Icon name="ArrowPathIcon" size={14} />
                          </button>

                          {/* Rotated Badge */}
                          {isRotated && (
                            <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                              <span className="text-[12px] font-bold text-amber-600 bg-white px-1.5 rounded-md">
                                Rotated
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
              </div>
            </div>
          </div>

          {/* ================= RIGHT SIDEBAR ================= */}
          <div className="w-full lg:w-[280px]">
            <div className="lg:sticky lg:top-6 space-y-4">
              <div className="p-3 rounded-xl border border-brand-border bg-brand-surface">
                <div className="grid grid-cols-3 gap-y-2 text-[11px] text-brand-muted">
                  <span className="flex items-center gap-1">
                    <Icon name="ArrowPathIcon" size={13} />
                    Rotate
                  </span>

                  <span className="flex items-center gap-1">
                    <Icon name="EyeIcon" size={13} />
                    Preview
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={rotateAll}
                  className="w-full py-3 rounded-xl font-heading font-semibold text-white"
                  style={{ background: '#F59E0B' }}
                >
                  Rotate All
                </button>

                <button
                  onClick={resetAll}
                  className="w-full py-3 rounded-xl border border-brand-border text-brand-mid font-semibold"
                >
                  Reset All
                </button>
              </div>

              {/* Progress */}
              {isProcessing && (
                <div>
                  <div className="flex justify-between text-xs font-bold text-brand-dark mb-1">
                    <span>Processing...</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 w-full bg-brand-surface border border-brand-border rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{ width: `${progress}%`, background: colors.primary }}
                    />
                  </div>
                </div>
              )}

              {/* Main Action */}
              {!isProcessing && (
                <button
                  onClick={handleRotate}
                  className="w-full py-4 rounded-2xl font-heading font-bold text-white"
                  style={{
                    background: colors.primary,
                    boxShadow: colors.shadow,
                  }}
                >
                  Rotate PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DONE STATE (UNCHANGED) */}
      {isDone && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
            <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
          </div>

          <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
            Rotation Complete!
          </h3>

          <p className="text-brand-mid text-[14px] font-body mb-6">
            Your PDF pages have been rotated exactly as requested.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
              onClick={handleDownload}
              style={{
                backgroundColor: ROTATE_THEME.primary,
                boxShadow: '0 6px 20px ' + ROTATE_THEME.primaryShadow,
              }}
            >
              <Icon name="ArrowDownTrayIcon" size={18} variant="solid" />
              Download Rotated PDF
            </button>
            <button
              onClick={handleReset}
              className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
            >
              <Icon name="ArrowPathIcon" size={18} />
              Rotate Another PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
